/**
 *
 * CustomTable
 *
 */

import React, { memo, useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button, Empty, Input, Space, Table } from 'antd';
import './style.scss';
import { SearchOutlined } from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import classNames from 'classnames';

import { useHistory } from 'react-router-dom';

function CustomTableV2({
  dataSource,
  columns,
  defaultPageSize,
  onChange,
  onSelectChange,
  hasSelection = false,
  hasPagination,
  rowKey,
  emptyText,
  expandable,
  customClass,
  rowSelectedKeys,
  duplicate,
  scroll,
  onRowLink = null,
  ...extraProps
}) {
  const history = useHistory();
  const [selectedRowKeys, SetSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');

  const useFocus = () => {
    const htmlElRef = useRef(null);
    const setFocus = () => {
      htmlElRef.current && htmlElRef.current.focus();
    };

    return [htmlElRef, setFocus];
  };

  useEffect(() => {
    if (JSON.stringify(rowSelectedKeys) !== JSON.stringify(selectedRowKeys)) {
      SetSelectedRowKeys(rowSelectedKeys);
    }
  }, [rowSelectedKeys]);

  const [inputRef, setInputFocus] = useFocus();

  const rowSelection = {
    selectedRowKeys,
    selections: true,
    onChange: r => {
      SetSelectedRowKeys(r);
      onSelectChange(r);
    },
  };

  const onRow = (record, rowIndex) => {
    return {
      onClick: event => {
        // record: row의 data
        // rowIndex: row의 index
        // event: event prototype
        if (event.target.className === 'link-table') {
          return false;
        } else {
          history.push(onRowLink(record));
        }
      },
    };
  };

  const getColumnSearchProps = (dataIndex, name, columnProps) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          autoFocus
          ref={inputRef}
          placeholder={`Rechercher dans`}
          value={selectedKeys[0]}
          onChange={e =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
          >
            Rechercher
          </Button>
          <Button
            onClick={() => handleReset(clearFilters, confirm)}
            size="small"
          >
            Réinitialiser
          </Button>
        </Space>
      </div>
    ),
    filterIcon: filtered => (
      <SearchOutlined
        style={{ color: filtered ? '#1890ff' : undefined }}
        onClick={setInputFocus}
      />
    ),
    onFilter: (value, record) => {
      const nameSplit = Array.isArray(dataIndex)
        ? dataIndex
        : dataIndex.split('.');

      if (columnProps.searchIn !== undefined) {
        return columnProps
          .searchIn(record)
          .toString()
          .toLowerCase()
          .includes(value.toLowerCase());
      }

      // Sub 2 level
      if (nameSplit.length === 2) {
        if (record[nameSplit[0]] !== undefined) {
          if (record[nameSplit[0]][nameSplit[1]] !== undefined) {
            return record[nameSplit[0]][nameSplit[1]]
              .toString()
              .toLowerCase()
              .includes(value.toLowerCase());
          }
        }
      }

      // search in 3 level
      if (nameSplit.length === 3) {
        if (record[nameSplit[0]] !== undefined) {
          if (record[nameSplit[0]][nameSplit[1]] !== undefined) {
            if (
              record[nameSplit[0]][nameSplit[1]][nameSplit[2]] !== undefined
            ) {
              return record[nameSplit[0]][nameSplit[1]][nameSplit[2]]
                .toString()
                .toLowerCase()
                .includes(value.toLowerCase());
            }
          }
        }
      }

      return record[dataIndex]
        ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase())
        : '';
    },
    onFilterDropdownVisibleChange: visible => {
      if (visible) {
        setTimeout(() => inputRef.current?.select(), 100);
      }
    },
    render: (text, record) =>
      searchedColumn === dataIndex
        ? columnProps.renderHighlight !== undefined
          ? columnProps.renderHighlight(
              text,
              record,
              <Highlighter
                highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                searchWords={[searchText]}
                autoEscape
                textToHighlight={text ? text.toString() : ''}
              />,
            )
          : columnProps.render(text, record)
        : columnProps.render !== undefined
        ? columnProps.render(text, record)
        : text,
  });

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters, confirm) => {
    clearFilters();
    setSearchText('');
    setSearchedColumn('');
    confirm();
  };

  let adaptedColumns = columns.map(col => {
    if (col.search) {
      return { ...col, ...getColumnSearchProps(col.dataIndex, col.title, col) };
    } else {
      return col;
    }
  });

  return (
    <div>
      <Table
        onRow={onRowLink === null ? () => {} : onRow}
        rowKey={rowKey}
        scroll={scroll ? scroll : { y: '60vh' }}
        className={classNames(
          'custom-table',
          customClass,
          onRowLink && 'rowHover',
        )}
        dataSource={dataSource}
        columns={adaptedColumns}
        size={'middle'}
        onChange={onChange}
        rowSelection={hasSelection !== false ? rowSelection : null}
        expandable={expandable}
        {...extraProps}
        pagination={
          hasPagination
            ? {
                hideOnSinglePage: false,
                position: ['bottomRight'],
                defaultPageSize: 20,
                pageSizeOptions: [10, 20, 50, 100],
                showSizeChanger: true,
              }
            : { hideOnSinglePage: true, defaultPageSize: 100 }
        }
        locale={{
          triggerDesc: 'Cliquez pour trier par ordre decroissant',
          triggerAsc: 'Cliquez pour trier par ordre croissant',
          cancelSort: 'Cliquez pour annuler le tri',
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={'Aucune donnée'}
            />
          ),
        }}
      />
    </div>
  );
}

CustomTableV2.propTypes = {
  hasSelection: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
};

CustomTableV2.defaultProps = {
  onSelectChange: () => {},
  rowKey: record => record._id,
  hasPagination: false,
};

export default memo(CustomTableV2);
